import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PartnerLogosComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { Plus, Trash2, Upload, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

interface PartnerLogosSettingsProps {
  component: PartnerLogosComponent;
}

const STYLE_OPTIONS = ['default', 'dark', 'light', 'gradient'] as const;

export const PartnerLogosSettings: React.FC<PartnerLogosSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingLogoId, setUploadingLogoId] = useState<string | null>(null);

  const handleUpdate = (updates: Partial<PartnerLogosComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const addLogo = () => {
    const newLogo = {
      id: `logo-${Date.now()}`,
      imageUrl: 'https://via.placeholder.com/200x80?text=Logo',
      name: t('settings.partnerLogos.defaultPartnerName'),
      link: '',
    };
    handleUpdate({ logos: [...component.settings.logos, newLogo] });
  };

  const removeLogo = (logoId: string) => {
    if (component.settings.logos.length <= 2) {
      toast.error(t('settings.partnerLogos.atLeastTwo'));
      return;
    }
    handleUpdate({
      logos: component.settings.logos.filter((logo) => logo.id !== logoId),
    });
  };

  const updateLogo = (logoId: string, updates: any) => {
    handleUpdate({
      logos: component.settings.logos.map((logo) =>
        logo.id === logoId ? { ...logo, ...updates } : logo
      ),
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, logoId: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('settings.partnerLogos.imageOnly'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('settings.partnerLogos.imageTooLarge5MB'));
      return;
    }

    setIsUploading(true);
    setUploadingLogoId(logoId);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      updateLogo(logoId, { imageUrl: data.url });
      toast.success(t('settings.partnerLogos.uploadSuccess'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('settings.partnerLogos.uploadFailed'));
    } finally {
      setIsUploading(false);
      setUploadingLogoId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Style Selection */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.partnerLogos.styleLabel')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STYLE_OPTIONS.map((key) => (
            <option key={key} value={key}>
              {t(`settings.partnerLogos.styles.${key}` as const)}
            </option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.partnerLogos.titleOptional')}</label>
        <input
          type="text"
          value={component.settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.partnerLogos.titlePlaceholder')}
        />
      </div>

      {/* Logo Height */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.partnerLogos.logoHeightLabel', { value: component.settings.logoHeight })}
        </label>
        <input
          type="range"
          min="40"
          max="120"
          value={component.settings.logoHeight}
          onChange={(e) => handleUpdate({ logoHeight: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      {/* Animation Speed */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.partnerLogos.animationSpeedLabel', { value: component.settings.animationSpeed })}
        </label>
        <input
          type="range"
          min="10"
          max="60"
          value={component.settings.animationSpeed}
          onChange={(e) => handleUpdate({ animationSpeed: parseInt(e.target.value) })}
          className="w-full"
        />
        <p className="text-xs text-gray-500 mt-1">{t('settings.partnerLogos.animationSpeedHint')}</p>
      </div>

      {/* Logos */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium">{t('settings.partnerLogos.partnerLogos')}</label>
          <button
            onClick={addLogo}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus size={16} />
            {t('settings.partnerLogos.addLogo')}
          </button>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {component.settings.logos.map((logo, index) => (
            <div
              key={logo.id}
              className="border border-gray-300 rounded-lg p-3 bg-gray-50"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {t('settings.partnerLogos.logoNumber', { n: index + 1 })}
                </span>
                {component.settings.logos.length > 2 && (
                  <button
                    onClick={() => removeLogo(logo.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {/* Logo Preview */}
              {logo.imageUrl && (
                <div className="mb-2">
                  <img
                    src={logo.imageUrl}
                    alt={logo.name}
                    className="h-16 object-contain border border-gray-300 rounded bg-white p-2"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x80?text=Logo';
                    }}
                  />
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading && uploadingLogoId === logo.id}
                className="w-full px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-2"
              >
                {isUploading && uploadingLogoId === logo.id ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    {t('settings.common.uploading')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {t('settings.partnerLogos.uploadLogo')}
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, logo.id)}
                className="hidden"
              />

              {/* Image URL */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('settings.partnerLogos.imageUrl')}
                </label>
                <input
                  type="text"
                  value={logo.imageUrl}
                  onChange={(e) => updateLogo(logo.id, { imageUrl: e.target.value })}
                  className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-xs"
                  placeholder={t('settings.partnerLogos.imageUrlPlaceholder')}
                />
              </div>

              {/* Name */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('settings.partnerLogos.partnerName')}
                </label>
                <input
                  type="text"
                  value={logo.name}
                  onChange={(e) => updateLogo(logo.id, { name: e.target.value })}
                  className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-xs"
                  placeholder={t('settings.partnerLogos.partnerNamePlaceholder')}
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('settings.partnerLogos.linkUrlOptional')}
                </label>
                <input
                  type="text"
                  value={logo.link}
                  onChange={(e) => updateLogo(logo.id, { link: e.target.value })}
                  className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-xs"
                  placeholder={t('settings.partnerLogos.linkPlaceholder')}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Background Color */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      {/* Padding */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.padding')}: {component.settings.padding}px
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};

